import { createContext, useContext } from 'react';

/**
 * Console-wide filter state, split from its provider so this module exports no
 * components — otherwise every constant here defeats React Fast Refresh for
 * the provider file. Same split the notification context already uses.
 *
 * Nothing in this dashboard could be scoped to a period: every panel showed an
 * all-time total, so "are we up on last month?" was unanswerable without
 * exporting. A single global range lives here so a panel opts in by reading it
 * rather than growing its own date picker, and so the choice survives a tab
 * switch.
 */

export type DateRangeKey = '7d' | '30d' | '90d' | 'ytd' | 'all';

export interface DateRangeOption {
  key: DateRangeKey;
  label: string;
  /** Wording for the delta line on metric cards. */
  comparedTo: string;
  /** Days in the window; null for ranges without a fixed length. */
  days: number | null;
}

export const DATE_RANGES: DateRangeOption[] = [
  { key: '7d', label: 'Last 7 days', comparedTo: 'vs previous 7 days', days: 7 },
  { key: '30d', label: 'Last 30 days', comparedTo: 'vs previous 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', comparedTo: 'vs previous 90 days', days: 90 },
  { key: 'ytd', label: 'Year to date', comparedTo: 'vs same period last year', days: null },
  { key: 'all', label: 'All time', comparedTo: '', days: null },
];

export const DATE_RANGE_STORAGE_KEY = 'wns.admin.dateRange';

export interface DashboardFiltersValue {
  range: DateRangeOption;
  setRange: (key: DateRangeKey) => void;
  /**
   * ISO start/end for the selected window, or nulls for "all time". Panels
   * pass these straight to the API once their endpoint accepts them.
   */
  bounds: { from: string | null; to: string | null };
}

export const DashboardFiltersContext = createContext<DashboardFiltersValue | null>(null);

export function useDashboardFilters(): DashboardFiltersValue {
  const context = useContext(DashboardFiltersContext);
  if (!context) {
    throw new Error('useDashboardFilters must be used inside a DashboardFiltersProvider');
  }
  return context;
}
